/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, TextField, Typography2 } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';

import useDefineGoalSuggestions from 'bundles/enterprise-admin-messaging-v2/hooks/useDefineGoalSuggestions';
import { AI_MESSAGE_ACTIONS } from 'bundles/enterprise-admin-messaging-v2/utils/aiMessageActions';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  prompt: string;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onCancel: () => void;
  /**
   * Hook for the "Draft a message manually" link. Stubbed as a no-op until
   * the manual-draft flow ships in a follow-up PR.
   */
  onDraftManually?: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  suggestionRow: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--cds-spacing-100);
  `,
  suggestion: css`
    /* Per Figma: width=hug capped at 443px, height=hug (36px on a single line),
       2px gradient border (blue-800 → blue-600), 8px radius, 8/16 padding,
       white bg. The double-background trick renders the gradient border
       cleanly with rounded corners. */
    align-items: center;
    background: linear-gradient(
          var(--cds-color-neutral-background-primary),
          var(--cds-color-neutral-background-primary)
        )
        padding-box,
      linear-gradient(180deg, var(--cds-color-blue-800), var(--cds-color-blue-600)) border-box;
    border: 2px solid transparent;
    border-radius: var(--cds-border-radius-100);
    color: var(--cds-color-interactive-secondary);
    cursor: pointer;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    text-align: left;

    &:hover:not(:disabled) {
      background: linear-gradient(
            var(--cds-color-interactive-background-primary-hover-weak),
            var(--cds-color-interactive-background-primary-hover-weak)
          )
          padding-box,
        linear-gradient(180deg, var(--cds-color-blue-800), var(--cds-color-blue-600)) border-box;
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  manualLink: css`
    margin-left: auto;
  `,
};

/**
 * Step 1 of the AI Message modal: the admin types (or picks) a natural-
 * language prompt describing the audience + goal. Pressing Generate hands
 * the prompt off to the orchestrator, which transitions to the Generating
 * loading state.
 */
const DefineGoalScreen = ({ prompt, onPromptChange, onGenerate, onCancel, onDraftManually }: Props) => {
  const trimmedPrompt = prompt.trim();
  const canGenerate = trimmedPrompt.length > 0;
  const { suggestions, refresh: refreshSuggestions } = useDefineGoalSuggestions();

  // Rotate the suggestion set in localStorage for the NEXT modal open. The
  // current chips stay stable for the rest of this session.
  const handleGenerate = React.useCallback(() => {
    refreshSuggestions();
    onGenerate();
  }, [refreshSuggestions, onGenerate]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <Typography2 component="p" variant="bodyPrimary">
            {_t('What and who do you want your message to focus on?')}
          </Typography2>
          <TextField
            aria-label={_t('Message goal')}
            multiline
            minRows={3}
            value={prompt}
            onChange={(event) => onPromptChange(event.target.value)}
            placeholder={_t(
              "e.g. Send a re-engagement message to learners who haven't been active in the last 2 weeks"
            )}
            data-testid="ai-message-modal-prompt-input"
          />
          <div css={styles.suggestionRow}>
            {suggestions.map((key) => {
              const action = AI_MESSAGE_ACTIONS[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onPromptChange(action.prompt)}
                  css={styles.suggestion}
                  data-testid={`ai-message-modal-suggestion-${key}`}
                >
                  <Typography2 component="span" variant="actionPrimary" color="inherit">
                    {action.prompt}
                  </Typography2>
                </button>
              );
            })}
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={styles.actions}>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={!canGenerate}
            icon={<AIGenerateIcon />}
            iconPosition="before"
            data-testid="ai-message-modal-generate"
          >
            {_t('Generate')}
          </Button>
          <Button variant="ghost" onClick={onCancel} data-testid="ai-message-modal-cancel">
            {_t('Cancel')}
          </Button>
          <Button
            variant="ghost"
            onClick={onDraftManually}
            css={styles.manualLink}
            data-testid="ai-message-modal-draft-manually"
          >
            {_t('Draft a message manually')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default DefineGoalScreen;
